import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators'
import { store } from '../../store'
import { notificationsApi } from '../../services/api/notifications.api'
import { Notification } from '../../models/notification'

@Module({
    dynamic: true,
    namespaced: true,
    name: 'notifications',
    store,
})
class NotificationsStore extends VuexModule {
    private items: Notification[] = []
    private serverUnreadCount = 0
    private loadingState = false

    get notifications() {
        return [...this.items].sort((a, b) => b.created_at.localeCompare(a.created_at))
    }

    get unread() {
        return this.notifications.filter(x => !x.read_at)
    }

    get unreadCount() {
        return Math.max(this.serverUnreadCount, this.unread.length)
    }

    get hasUnread() {
        return this.unreadCount > 0
    }

    get loading() {
        return this.loadingState
    }

    @Mutation
    upsert(items: Notification[]) {
        const byId = new Map(this.items.map(x => [x.id, x]))
        items.forEach(x => byId.set(x.id, x))
        this.items = [...byId.values()]
    }

    @Mutation
    setUnreadCount(count: number) {
        this.serverUnreadCount = Math.max(count, 0)
    }

    @Mutation
    setLoading(loading: boolean) {
        this.loadingState = loading
    }

    @Mutation
    setRead(ids: number[]) {
        const readAt = new Date().toISOString()
        this.items
            .filter(x => ids.includes(x.id))
            .forEach(x => x.read_at = readAt)
    }

    @Mutation
    clear() {
        this.items = []
        this.serverUnreadCount = 0
    }

    @Action({ rawError: true })
    async fetch() {
        this.setLoading(true)
        try {
            const response = await notificationsApi.getAll()
            this.upsert(response.messages)
            this.setUnreadCount(response.unread_count)
        } finally {
            this.setLoading(false)
        }
    }

    @Action({ rawError: true })
    receive(notification: Notification) {
        if (this.items.some(x => x.id === notification.id)) {
            return
        }
        this.upsert([notification])
        this.setUnreadCount(this.serverUnreadCount + 1)
    }

    @Action({ rawError: true })
    async markRead(ids: number[]) {
        const unreadIds = this.unread.filter(x => ids.includes(x.id)).map(x => x.id)
        if (!unreadIds.length) {
            return
        }
        this.setRead(unreadIds)
        this.setUnreadCount(this.serverUnreadCount - unreadIds.length)
        try {
            this.setUnreadCount(await notificationsApi.markRead(unreadIds))
        } catch (error) {
            console.error('Marking messages as read failed', error)
            await this.fetch()
        }
    }

    @Action({ rawError: true })
    async markAllRead() {
        this.setRead(this.unread.map(x => x.id))
        this.setUnreadCount(0)
        try {
            this.setUnreadCount(await notificationsApi.markAllRead())
        } catch (error) {
            console.error('Marking all messages as read failed', error)
            await this.fetch()
        }
    }
}

export const notificationsStore = getModule(NotificationsStore)
